/**
 * Admin Debug Panel
 * SECURITY: Only accessible to authenticated admins
 * Provides debug tools and information for development/debugging
 */

import { useState } from 'react'
import { useAuth } from '../contexts/AuthContextCore'
import { isDebugModeEnabled, isProductionMode } from '../lib/security'
import { logger } from '../lib/logger'
import { Bug, X, Copy, Check } from 'lucide-react'

export function AdminDebugPanel() {
  const { user } = useAuth()
  const userRole = user?.memberships?.[0]?.role
  const isAdmin = userRole === 'admin'
  const isDebug = isDebugModeEnabled(userRole || undefined)
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  // Only show to admins with debug mode enabled
  if (!isAdmin || !isDebug) {
    return null
  }

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopied(id)
    setTimeout(() => setCopied(null), 2000)
  }

  const debugInfo = {
    mode: isProductionMode() ? 'Production (Locked)' : 'Development',
    userRole,
    userId: user?.id,
    userEmail: user?.email,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    windowSize: `${window.innerWidth}x${window.innerHeight}`,
    localStorage: Object.keys(localStorage).length,
    sessionStorage: Object.keys(sessionStorage).length,
  }

  return (
    <>
      {/* Floating debug button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transition-colors"
        title="Admin Debug Panel"
        aria-label="Open Admin Debug Panel"
      >
        <Bug size={20} />
      </button>

      {/* Debug panel modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Admin Debug Panel
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Warning banner */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Admin Only:</strong> This panel contains sensitive debug information. 
                  Do not share screenshots or information from this panel.
                </p>
              </div>

              {/* Debug Information */}
              <section>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  Debug Information
                </h3>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2">
                  {Object.entries(debugInfo).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-start">
                      <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                        {key}:
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-gray-900 dark:text-gray-100 max-w-md truncate">
                          {String(value)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(String(value), key)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Copy to clipboard"
                        >
                          {copied === key ? (
                            <Check size={16} className="text-green-600" />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Environment Variables (safe ones only) */}
              <section>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  Environment (Safe Variables Only)
                </h3>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                      VITE_APP_MODE:
                    </span>
                    <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                      {import.meta.env.VITE_APP_MODE || 'not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                      MODE:
                    </span>
                    <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                      {import.meta.env.MODE}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                      DEV:
                    </span>
                    <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                      {String(import.meta.env.DEV)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                      PROD:
                    </span>
                    <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                      {String(import.meta.env.PROD)}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Note: Sensitive variables (API keys, secrets) are never exposed here.
                </p>
              </section>

              {/* Toggle Debug Mode */}
              <section>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  Debug Controls
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={localStorage.getItem('admin_debug_enabled') === 'true'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          localStorage.setItem('admin_debug_enabled', 'true')
                        } else {
                          localStorage.removeItem('admin_debug_enabled')
                        }
                        window.location.reload()
                      }}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Enable persistent debug mode (survives page reload)
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This setting is stored in localStorage and only affects your browser.
                  </p>
                </div>
              </section>

              {/* Actions */}
              <section>
                <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  Actions
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      logger.debug('Manual debug log from admin panel')
                      alert('Debug log sent to console')
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    Test Logger
                  </button>
                  <button
                    onClick={() => {
                      localStorage.clear()
                      sessionStorage.clear()
                      alert('Storage cleared. Page will reload.')
                      window.location.reload()
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                  >
                    Clear Storage
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

